<template>
  <v-dialog :value="true" max-width="500" persistent @input="close">
    <v-card>
      <v-card-title>Probar Envío</v-card-title>
      <v-card-text>
        <dl class="delivery-summary">
          <template v-for="row in summary">
            <dt :key="`label-${row.label}`">{{ row.label }}</dt>
            <dd :key="`value-${row.label}`">{{ row.value }}</dd>
          </template>
        </dl>

        <!-- El aviso no es decoracion: es la unica señal que tiene el usuario
             de que puede probar sin miedo. El backend garantiza por
             estructura que el correo sale exclusivamente a esta direccion
             -sendDeliveryTest no tiene ramas de cc ni de bcc, ver
             mail.service.js-, pero la pantalla tiene que dejarlo claro ANTES
             de que se aprete Enviar. -->
        <v-alert type="warning" dense text prominent border="left">
          El correo de prueba se manda <strong>únicamente</strong> a la
          dirección que escribas abajo. No le llega a la empresa ni a ninguna
          contratista.
        </v-alert>

        <v-text-field
          v-model="toEmail"
          label="Correo de destino"
          :error-messages="fieldErrorMessage"
          :disabled="sending"
          autofocus
          @keyup.enter="send"
        />

        <!-- Opcional: sin elegir nada, la prueba usa la primera contratista con
             algo para informar, que es como venia funcionando. Elegir una
             sirve para ver como le llegaria a esa en particular. -->
        <v-select
          v-if="contractors.length"
          v-model="contractorCompany"
          :items="contractorItems"
          item-text="label"
          item-value="value"
          label="Contratista"
          :disabled="sending || loadingContractors"
          :loading="loadingContractors"
        />

        <v-checkbox
          v-if="attachesSheet"
          v-model="asXlsx"
          label="Enviar planilla en XLS"
          :disabled="sending"
          dense
          hide-details
          class="mb-4"
        />

        <!-- El resumen consolidado no se puede probar por contratista: el
             correo real es uno solo con todas. Se dice en vez de esconder el
             selector sin explicacion. -->
        <v-alert v-if="digestMode" type="info" dense text>
          Esta empresa recibe un resumen consolidado con todas sus contratistas,
          así que la prueba muestra ese correo y no el de una en particular.
        </v-alert>

        <v-alert v-if="result && result.sent" type="success" dense text>
          Se envió correctamente a {{ sentTo }}.
        </v-alert>

        <!-- sent:false con reason no es una falla -por ejemplo, "hoy no hay
             nada para informar"-, asi que se muestra tal cual vino del
             backend y no como un error: disfrazarlo de error haria creer que
             la configuracion del Envio esta mal cuando esta bien. -->
        <v-alert v-if="result && !result.sent" type="info" dense text>
          {{ result.reason }}
        </v-alert>

        <v-alert v-if="requestErrorMessage" type="error" dense text>{{ requestErrorMessage }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="sending" @click="close">Cerrar</v-btn>
        <v-btn
          class="primary"
          :loading="sending"
          :disabled="!isValidEmail || sending"
          @click="send"
        >Enviar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
"use strict";

import emailDeliveryService from "@/services/EmailDeliveryService";

// Mismo patron que email-list.component.vue, para no tener dos criterios de
// "correo valido" distintos en la misma pantalla.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: "delivery-test-dialog",
  props: {
    deliveryId: { type: String, required: true },
    // Fin de mes con planilla: la prueba puede mandarla en Excel en vez de PDF.
    attachesSheet: { type: Boolean, default: false },
    // [{ label, value }]: los datos de la fila elegida.
    summary: { type: Array, default: () => [] }
  },
  data() {
    return {
      toEmail: "",
      // La direccion que efectivamente se mando, congelada al momento del
      // envio: si el usuario edita el campo despues de un envio exitoso, la
      // confirmacion tiene que seguir nombrando a donde fue el correo que ya
      // salio, no lo que haya quedado tipeado despues.
      sentTo: "",
      sending: false,
      result: null,
      requestErrorMessage: null,
      // Vacio = "la primera con algo para informar", el comportamiento previo.
      contractorCompany: "",
      asXlsx: false,
      contractors: [],
      digestMode: false,
      loadingContractors: false
    };
  },
  computed: {
    normalizedEmail() {
      return (this.toEmail || "").trim().toLowerCase();
    },
    isValidEmail() {
      return EMAIL_PATTERN.test(this.normalizedEmail);
    },
    // Sin mensaje mientras el campo esta vacio: recien se avisa "invalido"
    // cuando hay algo tipeado que no matchea, no antes de que el usuario
    // empiece a escribir.
    contractorItems() {
      return [{ value: "", label: "La primera con algo para informar" }].concat(
        this.contractors.map(c => ({ value: c._id, label: c.name })));
    },
    fieldErrorMessage() {
      return this.toEmail && !this.isValidEmail ? "Correo inválido" : null;
    }
  },
  async mounted() {
    await this.loadContractors();
  },
  methods: {
    // Si falla, la pantalla sigue sirviendo: sin selector, la prueba se
    // comporta como antes. No se muestra error por esto, porque no impide
    // hacer lo que el usuario vino a hacer.
    async loadContractors() {
      this.loadingContractors = true;
      try {
        const data = await emailDeliveryService.eligibleContractors(this.deliveryId);
        this.contractors = (data && data.contractors) || [];
        this.digestMode = !!(data && data.digestMode);
      } catch (error) {
        this.contractors = [];
      } finally {
        this.loadingContractors = false;
      }
    },
    messageOf(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || fallback;
    },
    close() {
      this.$emit("close");
    },
    async send() {
      if (!this.isValidEmail || this.sending) return;
      this.requestErrorMessage = null;
      this.result = null;
      this.sending = true;
      try {
        const toEmail = this.normalizedEmail;
        // El unico dato de DESTINATARIO que viaja es esta direccion. La
        // contratista elegida no es un destinatario: define que contenido se
        // renderiza, y el correo sigue yendo exclusivamente a `toEmail`.
        this.result = await emailDeliveryService.sendTest(
          this.deliveryId, toEmail, this.contractorCompany || undefined, this.asXlsx);
        this.sentTo = toEmail;
      } catch (error) {
        this.requestErrorMessage = this.messageOf(error, "No se pudo enviar el correo de prueba");
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style lang="scss">
.delivery-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 16px;
  margin-bottom: 16px;
  dt {
    color: rgba(0, 0, 0, 0.6);
  }
  dd {
    margin: 0;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 500;
    overflow-wrap: anywhere;
  }
}
</style>
